import { log } from '@/log'
import { HlAppMode } from '@/models/hl-app/hl-app-mode'
import { PreAutoLogin, PreAutoLoginNoCascade } from '@/services/auth/call-with-auth/pre-auto-login'
import { PreRegisterProvisional } from '@/services/auth/call-with-auth/pre-register-provisional'
import { PreRehashCurrentUserData } from '@/services/auth/call-with-auth/pre-rehash-current-user-data'
import { CookieService } from '@/services/cookie'
import { AutoLoginService } from '@ajax/modules/services/auto-login'
import { UserService } from '@ajax/modules/services/user'

/**
 * 事前実行される認証処理の設定
 */
export interface PreAuthenticationConfig {
  /**
   * 識別子
   */
  readonly id: string
  /**
   * @return 事前処理実行の条件を満たす場合にtrue
   */
  processable(): boolean
  /**
   * @return trueの場合、事前処理が実行されかつ失敗した時に、後続の事前処理を試行する。
   * TODO: 事前処理を定義するクラスそのものに固定的にフラグを設定しているが、事前処理を組み合わす側でユースケースに応じてフラグを注入できるようにしてもよい。
   */
  cascade(): boolean
  /**
   * 事前処理を実行する
   * この実行結果Promiseが成功（resolve）か失敗（reject）かに応じて、事前認証処理の失敗時のカスケードが発動する、という依存関係に注意。
   * @see https://github.com/my-color/growthpalette-front/pull/7702#discussion_r1390889801
   */
  process(): Promise<void>
}

/**
 * 指定された「事前認証処理」の内、先頭から順番に実行可能なものを判定した上で、
 * - 実行可能な処理のうち、高々一つだけの処理の実行が成功する。ある処理の実行が成功したら残りの処理は実行されない。
 * - 実行された処理は成功または失敗するが、「その処理が失敗」かつ「その処理のカスケードフラグがON」の場合のみ、後続処理を試行する。
 * - したがって、複数の処理が実行されるパターンはありえるが、それは前段のカスケードONな処理が実行可能かつ失敗した場合のみ、である。
 *
 * 実行可能な処理が1つもなかった場合は何もしない。
 *
 * 取得の際に外部依存が発生し得るため、設定をパラメータとして受け取りそのIFにのみ依存することで
 * 関数の参照透過性の維持を試みている。
 *
 * ただし、外部依存性はそれが受け取られた時点で内容が確定するわけではなく、各事前認証処理の結果によってその内容が実質的に変化する可能性はある。
 * たとえば、CookieServiceを外部依存性として受け取った場合、各事前認証処理ごとの処理可能性チェックにおいて、「その時点における」クッキーの状態が参照される。
 * そのため、前段の処理の結果、後続の処理が不要になる、ということがありえる。
 */

const callAfterPreAuthentication = (stateStore: AuthStateStore, configs: PreAuthenticationConfig[]) => {
  let cache: Promise<void> | null = null

  const runPreAuthProcess = async () => {
    for (const config of configs) {
      if (!config.processable()) continue

      try {
        await config.process()
        log.debug(`${config.id} has been proceeded`)

        break
      } catch (error) {
        log.error({ error })

        if (config.cascade()) continue

        throw error
      }
    }

    if (!stateStore.hasAccessToken()) {
      throw new Error('auth token does not exist after pre auth processes')
    }
  }

  return async <T>(callback: () => Promise<T>): Promise<T> => {
    if (cache) {
      await cache
    } else {
      const p = runPreAuthProcess()
      cache = p

      await p.catch((error) => {
        // 成功時のみキャッシュを残す.
        // 失敗した場合はキャッシュを破棄して、後から再実行可能にしておく.
        cache = null

        return Promise.reject(error)
      })
    }

    return callback()
  }
}

export interface AuthStateStore {
  hasAccessToken(): boolean
  hasUserId(): boolean

  accessedURL(): URL
}
export interface AuthenticationGateway {
  // 実装簡略化 + 具象のシグニチャが変わったら連動した方が（初期実装時点では）楽なので
  // Parameters および ReturnType を利用しているが、
  // 具象のシグニチャとは完全に分離した方が都合がよくなったら、独立した定義にして良い.
  autoLogin: AutoLoginService['autoLogin']
  registerProvisional: UserService['registerProvisional']
  rehash: UserService['rehash']
}

export const buildCallWithAuth = (
  stateStore: AuthStateStore,
  gateway: AuthenticationGateway,
  pageContext: GrowthPalettePageContext
): <T>(callback: () => Promise<T>) => Promise<T> => {
  /**
   * ホテラバアプリWebViewでの閲覧、または、ホテラバアプリ用SPAでは、
   * 1. o, hashがあれば自動ログインを試みる
   * 2. 1.で自動ログインを試みたが失敗すれば中断
   * 3. 事後状態としてa cookie (auth token) の存在を確認し、存在しなければエラー
   * @see https://my-color.esa.io/posts/1362
   */
  if (HlAppMode.isViewedInHlAppWebView || HlAppMode.isPageForHlApp) {
    return callAfterPreAuthentication(stateStore, [
      new PreAutoLoginNoCascade(stateStore, gateway, pageContext),
    ])
  }

  return callAfterPreAuthentication(stateStore, [
    /*
    * 自動ログインと「ユーザー情報が無い or 欠落していたら補完する」という処理は
    * ユースケースとしては必ずしも同一タイミングで実施する必要の無いものだが、
    * パフォーマンスおよび制御の観点からここで一元的に処理している.
    * 詳細は以下を参照.
    * - https://github.com/my-color/front/issues/6989
    * - https://github.com/my-color/front/issues/6989#issuecomment-1293330027
    */
    new PreAutoLogin(stateStore, gateway, pageContext),
    new PreRegisterProvisional(stateStore, gateway, pageContext),
    new PreRehashCurrentUserData(stateStore, gateway, pageContext),
  ])
}

/**
 * 循環参照によるモジュール評価のエラーを回避するためにcallWithAuthの生成を遅延している
 *
 * - このファイルの読み込み時にはUserService, AutoLoginServiceのnewは実行しない
 * - callWithAuth() が始めて実行されたタイミングでUserService, AutoLoginServiceのnewが実行される
 * - 2回目移行の callWithAuth() ではcallWithAuthInstanceを返すのでnewは実行されない
 */
type CallWithAuthType = <T>(callback: () => Promise<T>) => Promise<T>
let callWithAuthInstance: CallWithAuthType | null = null
const getCallWithAuth = (): CallWithAuthType => {
  if (!callWithAuthInstance) {
    callWithAuthInstance = buildCallWithAuth(
      Object.assign({ accessedURL: () => new URL(location.href) }, CookieService),
      (() => {
        const user = new UserService()
        const autoLogin = new AutoLoginService()

        return {
          registerProvisional: () => user.registerProvisional(),
          rehash: () =>  user.rehash(),
          autoLogin: (u: string, h: string, s: string) => autoLogin.autoLogin(u, h, s),
        }
      })(),
      window.gpPageContext
    )
  }

  // callWithAuthInstanceは必ず初期化されている
  return callWithAuthInstance
}

/**
 * 事前に確保されるべき認証情報をすべてそろえてからコールバックを実行する.
 * ユーザー依存の情報を参照/更新するAPIは、基本的にこれを使って
 * 認証情報が揃っていることを確保した上で実行する.
 */
export const callWithAuth = <T>(callback: () => Promise<T>): Promise<T> => {
  return getCallWithAuth()(callback)
}
